/**
 * useGridSelection — Spreadsheet-like cell, column, and row selection for data grids.
 *
 * Supports single/multi select via Ctrl/Cmd+click and Shift+click range selection.
 * Provides Ctrl/Cmd+C copy-to-clipboard in TSV format and Escape to clear.
 */
import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';

// ── Types ────────────────────────────────────────────────────────────

export type SelectionMode = 'cell' | 'column' | 'row' | null;

/** Serialized cell key "rowIndex:colIndex" for O(1) Set lookup. */
type CellKey = `${number}:${number}`;

interface GridSelectionState {
  mode: SelectionMode;
  cells: Set<CellKey>;
  columns: Set<number>;
  rows: Set<number>;
  /** Anchor index for Shift+click range (row/column index). */
  anchor: number | null;
  /** Anchor cell for Shift+click rectangular range. */
  cellAnchor: { row: number; col: number } | null;
}

export interface SelectionDescriptor {
  mode: SelectionMode;
  selectedData: string[][];
  columnNames: string[];
  rowIndices: number[];
}

// ── Reducer ──────────────────────────────────────────────────────────

type Action =
  | { type: 'SELECT_CELL'; row: number; col: number; multi: boolean; range: boolean }
  | { type: 'SELECT_COLUMN'; col: number; multi: boolean; range: boolean }
  | { type: 'SELECT_ROW'; row: number; multi: boolean; range: boolean }
  | { type: 'SELECT_ALL'; totalRows: number; totalCols: number }
  | { type: 'DRAG_EXTEND_CELL'; row: number; col: number }
  | { type: 'DRAG_EXTEND_ROW'; row: number }
  | { type: 'DRAG_EXTEND_COLUMN'; col: number }
  | { type: 'CLEAR' };

const EMPTY_STATE: GridSelectionState = {
  mode: null,
  cells: new Set(),
  columns: new Set(),
  rows: new Set(),
  anchor: null,
  cellAnchor: null,
};

function makeRange(a: number, b: number): number[] {
  const min = Math.min(a, b);
  const max = Math.max(a, b);
  const result: number[] = [];
  for (let i = min; i <= max; i++) result.push(i);
  return result;
}

function makeCellKey(row: number, col: number): CellKey {
  return `${row}:${col}`;
}

function selectionReducer(state: GridSelectionState, action: Action): GridSelectionState {
  switch (action.type) {
    case 'CLEAR':
      return EMPTY_STATE;

    case 'SELECT_CELL': {
      const { row, col, multi, range } = action;
      const key = makeCellKey(row, col);

      if (range && state.cellAnchor && state.mode === 'cell') {
        // Rectangular range from anchor to current
        const newCells = new Set<CellKey>();
        const rows = makeRange(state.cellAnchor.row, row);
        const cols = makeRange(state.cellAnchor.col, col);
        for (const r of rows) {
          for (const c of cols) {
            newCells.add(makeCellKey(r, c));
          }
        }
        return { ...state, cells: newCells };
      }

      if (multi && state.mode === 'cell') {
        const newCells = new Set(state.cells);
        if (newCells.has(key)) {
          newCells.delete(key);
        } else {
          newCells.add(key);
        }
        return { ...state, cells: newCells, cellAnchor: { row, col } };
      }

      // Single select
      return {
        mode: 'cell',
        cells: new Set([key]),
        columns: new Set(),
        rows: new Set(),
        anchor: null,
        cellAnchor: { row, col },
      };
    }

    case 'SELECT_COLUMN': {
      const { col, multi, range } = action;

      if (range && state.anchor !== null && state.mode === 'column') {
        const indices = makeRange(state.anchor, col);
        return { ...state, columns: new Set(indices) };
      }

      if (multi && state.mode === 'column') {
        const newCols = new Set(state.columns);
        if (newCols.has(col)) {
          newCols.delete(col);
        } else {
          newCols.add(col);
        }
        return { ...state, columns: newCols, anchor: col };
      }

      return {
        mode: 'column',
        cells: new Set(),
        columns: new Set([col]),
        rows: new Set(),
        anchor: col,
        cellAnchor: null,
      };
    }

    case 'SELECT_ROW': {
      const { row, multi, range } = action;

      if (range && state.anchor !== null && state.mode === 'row') {
        const indices = makeRange(state.anchor, row);
        return { ...state, rows: new Set(indices) };
      }

      if (multi && state.mode === 'row') {
        const newRows = new Set(state.rows);
        if (newRows.has(row)) {
          newRows.delete(row);
        } else {
          newRows.add(row);
        }
        return { ...state, rows: newRows, anchor: row };
      }

      return {
        mode: 'row',
        cells: new Set(),
        columns: new Set(),
        rows: new Set([row]),
        anchor: row,
        cellAnchor: null,
      };
    }

    case 'SELECT_ALL': {
      const { totalRows, totalCols } = action;
      // If already all selected, deselect
      if (state.mode === 'cell' && state.cells.size === totalRows * totalCols) {
        return EMPTY_STATE;
      }
      const allCells = new Set<CellKey>();
      for (let r = 0; r < totalRows; r++) {
        for (let c = 0; c < totalCols; c++) {
          allCells.add(makeCellKey(r, c));
        }
      }
      return {
        mode: 'cell',
        cells: allCells,
        columns: new Set(),
        rows: new Set(),
        anchor: null,
        cellAnchor: null,
      };
    }

    case 'DRAG_EXTEND_CELL': {
      const { row, col } = action;
      if (!state.cellAnchor || state.mode !== 'cell') return state;
      const newCells = new Set<CellKey>();
      const rowRange = makeRange(state.cellAnchor.row, row);
      const colRange = makeRange(state.cellAnchor.col, col);
      for (const r of rowRange) {
        for (const c of colRange) {
          newCells.add(makeCellKey(r, c));
        }
      }
      return { ...state, cells: newCells };
    }

    case 'DRAG_EXTEND_ROW': {
      const { row } = action;
      if (state.anchor === null || state.mode !== 'row') return state;
      return { ...state, rows: new Set(makeRange(state.anchor, row)) };
    }

    case 'DRAG_EXTEND_COLUMN': {
      const { col } = action;
      if (state.anchor === null || state.mode !== 'column') return state;
      return { ...state, columns: new Set(makeRange(state.anchor, col)) };
    }

    default:
      return state;
  }
}

// ── TSV Builder ──────────────────────────────────────────────────────

function buildTsv(
  state: GridSelectionState,
  columns: string[],
  rows: Array<Record<string, unknown>>,
): string {
  const formatVal = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'object') {
      try { return JSON.stringify(v); } catch { return String(v); }
    }
    return String(v);
  };

  const lines: string[] = [];

  switch (state.mode) {
    case 'column': {
      const sortedCols = Array.from(state.columns).sort((a, b) => a - b);
      const colNames = sortedCols.map((i) => columns[i]);
      lines.push(colNames.join('\t'));
      for (const row of rows) {
        lines.push(colNames.map((c) => formatVal(row[c])).join('\t'));
      }
      break;
    }
    case 'row': {
      const sortedRows = Array.from(state.rows).sort((a, b) => a - b);
      lines.push(columns.join('\t'));
      for (const ri of sortedRows) {
        if (ri >= 0 && ri < rows.length) {
          lines.push(columns.map((c) => formatVal(rows[ri][c])).join('\t'));
        }
      }
      break;
    }
    case 'cell': {
      // Find bounding box of selected cells
      let minR = Infinity, maxR = -1, minC = Infinity, maxC = -1;
      Array.from(state.cells).forEach((key) => {
        const [r, c] = key.split(':').map(Number);
        if (r < minR) minR = r;
        if (r > maxR) maxR = r;
        if (c < minC) minC = c;
        if (c > maxC) maxC = c;
      });
      if (maxR < 0) break;
      // Header for selected columns
      const colNames = [];
      for (let c = minC; c <= maxC; c++) colNames.push(columns[c]);
      lines.push(colNames.join('\t'));
      // Rows
      for (let r = minR; r <= maxR; r++) {
        const vals: string[] = [];
        for (let c = minC; c <= maxC; c++) {
          if (state.cells.has(makeCellKey(r, c))) {
            vals.push(formatVal(rows[r]?.[columns[c]]));
          } else {
            vals.push('');
          }
        }
        lines.push(vals.join('\t'));
      }
      break;
    }
  }

  return lines.join('\n');
}

// ── Hook ─────────────────────────────────────────────────────────────

interface UseGridSelectionOpts {
  columns: string[];
  rows: Array<Record<string, unknown>>;
  page: number;
  rowsPerPage: number;
  onSelectionChange?: (sel: SelectionDescriptor | null) => void;
}

export function useGridSelection({
  columns,
  rows,
  page,
  rowsPerPage,
  onSelectionChange,
}: UseGridSelectionOpts) {
  const [state, dispatch] = useReducer(selectionReducer, EMPTY_STATE);
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<{ mode: 'cell' | 'row' | 'column' } | null>(null);

  // ── Drag handlers (mousemove/mouseup on document during drag) ───
  const handleDragMove = useCallback(
    (rowIdx: number, colIdx: number) => {
      if (!draggingRef.current) return;
      const mode = draggingRef.current.mode;
      if (mode === 'cell') {
        dispatch({ type: 'DRAG_EXTEND_CELL', row: rowIdx, col: colIdx });
      } else if (mode === 'row') {
        dispatch({ type: 'DRAG_EXTEND_ROW', row: rowIdx });
      } else if (mode === 'column') {
        dispatch({ type: 'DRAG_EXTEND_COLUMN', col: colIdx });
      }
    },
    [],
  );

  const handleDragEnd = useCallback(() => {
    draggingRef.current = null;
  }, []);

  // Global mouseup to end drag even if mouse leaves the table
  useEffect(() => {
    const onMouseUp = () => { draggingRef.current = null; };
    document.addEventListener('mouseup', onMouseUp);
    return () => document.removeEventListener('mouseup', onMouseUp);
  }, []);

  // ── Handlers ────────────────────────────────────────────────────
  const handleCellMouseDown = useCallback(
    (rowIdx: number, colIdx: number, e: React.MouseEvent) => {
      // Don't interfere with right-click context menus
      if (e.button !== 0) return;
      e.preventDefault();
      dispatch({
        type: 'SELECT_CELL',
        row: rowIdx,
        col: colIdx,
        multi: e.metaKey || e.ctrlKey,
        range: e.shiftKey,
      });
      if (!e.shiftKey) {
        draggingRef.current = { mode: 'cell' };
      }
    },
    [],
  );

  const handleCellMouseEnter = useCallback(
    (rowIdx: number, colIdx: number) => {
      handleDragMove(rowIdx, colIdx);
    },
    [handleDragMove],
  );

  const handleColumnSelect = useCallback(
    (colIdx: number, e: React.MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      dispatch({
        type: 'SELECT_COLUMN',
        col: colIdx,
        multi: e.metaKey || e.ctrlKey,
        range: e.shiftKey,
      });
      if (!e.shiftKey) {
        draggingRef.current = { mode: 'column' };
      }
    },
    [],
  );

  const handleColumnMouseEnter = useCallback(
    (colIdx: number) => {
      if (draggingRef.current?.mode === 'column') {
        dispatch({ type: 'DRAG_EXTEND_COLUMN', col: colIdx });
      }
    },
    [],
  );

  const handleRowSelect = useCallback(
    (rowIdx: number, e: React.MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      dispatch({
        type: 'SELECT_ROW',
        row: rowIdx,
        multi: e.metaKey || e.ctrlKey,
        range: e.shiftKey,
      });
      if (!e.shiftKey) {
        draggingRef.current = { mode: 'row' };
      }
    },
    [],
  );

  const handleRowMouseEnter = useCallback(
    (rowIdx: number) => {
      if (draggingRef.current?.mode === 'row') {
        dispatch({ type: 'DRAG_EXTEND_ROW', row: rowIdx });
      }
    },
    [],
  );

  const handleSelectAll = useCallback(
    () => {
      dispatch({ type: 'SELECT_ALL', totalRows: rows.length, totalCols: columns.length });
    },
    [rows.length, columns.length],
  );

  const clearSelection = useCallback(() => dispatch({ type: 'CLEAR' }), []);

  // ── Query helpers (O(1) per cell) ───────────────────────────────
  const isCellSelected = useCallback(
    (rowIdx: number, colIdx: number): boolean => {
      switch (state.mode) {
        case 'cell':
          return state.cells.has(makeCellKey(rowIdx, colIdx));
        case 'column':
          return state.columns.has(colIdx);
        case 'row':
          return state.rows.has(rowIdx);
        default:
          return false;
      }
    },
    [state],
  );

  const isColumnSelected = useCallback(
    (colIdx: number): boolean => state.mode === 'column' && state.columns.has(colIdx),
    [state.mode, state.columns],
  );

  const isRowSelected = useCallback(
    (rowIdx: number): boolean => state.mode === 'row' && state.rows.has(rowIdx),
    [state.mode, state.rows],
  );

  const hasSelection = state.mode !== null &&
    (state.cells.size > 0 || state.columns.size > 0 || state.rows.size > 0);

  // ── Keyboard: Copy + Escape ─────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dispatch({ type: 'CLEAR' });
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'c' && hasSelection) {
        e.preventDefault();
        const tsv = buildTsv(state, columns, rows);
        if (tsv) navigator.clipboard.writeText(tsv);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
        e.preventDefault();
        dispatch({ type: 'SELECT_ALL', totalRows: rows.length, totalCols: columns.length });
      }
    };

    el.addEventListener('keydown', handler);
    return () => el.removeEventListener('keydown', handler);
  }, [state, hasSelection, columns, rows]);

  // ── Clear on page/data change ───────────────────────────────────
  useEffect(() => {
    dispatch({ type: 'CLEAR' });
  }, [page, rowsPerPage]);

  // ── Notify parent ───────────────────────────────────────────────
  useEffect(() => {
    if (!onSelectionChange) return;
    if (!hasSelection) {
      onSelectionChange(null);
      return;
    }
    const formatVal = (v: unknown): string => {
      if (v === null || v === undefined) return '';
      return String(v);
    };
    const desc: SelectionDescriptor = {
      mode: state.mode,
      selectedData: [],
      columnNames: [],
      rowIndices: [],
    };

    switch (state.mode) {
      case 'column': {
        const sorted = Array.from(state.columns).sort((a, b) => a - b);
        desc.columnNames = sorted.map((i) => columns[i]);
        desc.selectedData = rows.map((row) => sorted.map((i) => formatVal(row[columns[i]])));
        desc.rowIndices = rows.map((_, i) => page * rowsPerPage + i);
        break;
      }
      case 'row': {
        const sorted = Array.from(state.rows).sort((a, b) => a - b);
        desc.columnNames = columns;
        desc.rowIndices = sorted.map((i) => page * rowsPerPage + i);
        desc.selectedData = sorted
          .filter((i) => i >= 0 && i < rows.length)
          .map((i) => columns.map((c) => formatVal(rows[i][c])));
        break;
      }
      case 'cell': {
        let minR = Infinity, maxR = -1, minC = Infinity, maxC = -1;
        Array.from(state.cells).forEach((key) => {
          const [r, c] = key.split(':').map(Number);
          if (r < minR) minR = r;
          if (r > maxR) maxR = r;
          if (c < minC) minC = c;
          if (c > maxC) maxC = c;
        });
        if (maxR >= 0) {
          for (let c = minC; c <= maxC; c++) desc.columnNames.push(columns[c]);
          for (let r = minR; r <= maxR; r++) {
            desc.rowIndices.push(page * rowsPerPage + r);
            desc.selectedData.push(
              Array.from({ length: maxC - minC + 1 }, (_, ci) =>
                state.cells.has(makeCellKey(r, minC + ci))
                  ? formatVal(rows[r]?.[columns[minC + ci]])
                  : '',
              ),
            );
          }
        }
        break;
      }
    }

    onSelectionChange(desc);
  }, [state, hasSelection, columns, rows, page, rowsPerPage, onSelectionChange]);

  return {
    state,
    isCellSelected,
    isColumnSelected,
    isRowSelected,
    hasSelection,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleColumnSelect,
    handleColumnMouseEnter,
    handleRowSelect,
    handleRowMouseEnter,
    handleSelectAll,
    clearSelection,
    containerRef,
  };
}
